import { loadCategoryList, loadEfficacyCombinedList, loadEfficacyList } from 'clients/CatClientV2';
import { useCallback } from 'react';
import { TTL_CACHE_SERVER } from 'sysconfig';
import { convertArrayToMap } from 'utils/ArrUtils';
import { CACHE_KEY, deserializeFunc, getWithExpiry, serializeFunc, setWithExpiry } from 'utils/localStorageUtils';

export function useCategories({ ttl = TTL_CACHE_SERVER * 1000 } = {}) {
  const FIELDS = ['code', 'name', 'slug', 'subEfficacies', 'relatedEfficacyCodes', 'relatedSellerCategoryCodes', 'relatedCategoryCodes', 'icon'];

  const getAllCategories = useCallback(async () => {
    // Case 1: cache hit
    const cachedCategories = getWithExpiry(CACHE_KEY.CATEGORIES);
    if (cachedCategories) {
      return cachedCategories.map((data) => deserializeFunc(data, FIELDS));
    }

    // Case 2: cache miss -> Fetch missing data, then cache
    const [categoryList, efficacyList, efficacyCombinedList] = await Promise.all([
      loadCategoryList(),
      loadEfficacyList(),
      loadEfficacyCombinedList(),
    ]);

    const combinedEfficacies = (efficacyCombinedList || []).reduce((acc, item) => {
      if (item.isActive) {
        acc.push({
          code: item.code,
          name: item.name,
          slug: item.slug,
          relatedCategoryCodes: item.relatedCategoryCodes || [],
          relatedEfficacyCodes: item.relatedEfficacyCodes || [],
          relatedSellerCategoryCodes: item.relatedSellerCategoryCodes || [],
          icon: item.icon,
        });
      }
      return acc;
    }, []);

    const categories = (categoryList || []).reduce((acc, item) => {
      if (item.isActive) {
        const subEfficacies = (efficacyList || [])
          .reduce((effAcc, efficacy) => {
            if (efficacy.categoryCode === item.code) {
              effAcc.push({
                code: efficacy.code,
                name: efficacy.name,
                slug: efficacy.slug,
                categoryCode: efficacy.categoryCode,
                icon: efficacy.icon,
              });
            }
            return effAcc;
          }, [])
          .concat(combinedEfficacies.filter((item) => item.relatedCategoryCodes?.includes(item.code)));

        acc.push({
          code: item.code,
          name: item.name,
          slug: item.slug,
          subEfficacies: subEfficacies,
          icon: item.icon,
        });
      }
      return acc;
    }, []);

    // Update cache
    setWithExpiry(
      CACHE_KEY.CATEGORIES,
      categories.map((category) => serializeFunc(category, FIELDS)),
      { ttl },
    );

    return categories;
  }, []);

  const getCategoryMapByCode = useCallback(async () => {
    const categories = await getAllCategories({ ttl });
    return convertArrayToMap(categories, 'code');
  }, []);

  return { getAllCategories, getCategoryMapByCode };
}
